import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Banner, BlockStack, Box, Button, Checkbox, Icon, InlineStack, List, Modal, Select, Text, TextField } from '@shopify/polaris';
import { ChevronRightIcon, ExternalIcon } from '@shopify/polaris-icons';
import { LINK } from '@/constants/link';
import { PROXY_PREFIXES, buildTrackingPageUrl, splitProxyPath, validateProxySlug } from '@/helpers/trackingPageUrl';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import useTrackingPageUrl from '@/hooks/useTrackingPageUrl';
import { useSyncTrackingUrlToShopifyMutation, useUpdateProxyPathMutation } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import commonSlice from '@/redux/features/common.slice';
import settingsSlice from '@/redux/features/settings.slice';

type Step = 'choose' | 'shopify' | 'success';

const urlBoxStyle = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  wordBreak: 'break-all' as const,
};

const stepDotStyle = (isActive: boolean) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: isActive ? '#303030' : '#c9cccf',
});

const breadcrumbStyle = { display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' };

const BreadcrumbPath = ({ items }: { items: string[] }) => (
  <span style={breadcrumbStyle}>
    {items.map((item, i) => (
      <span key={item} style={breadcrumbStyle}>
        {i > 0 && (
          <span style={{ display: 'inline-flex', width: 16, height: 16 }}>
            <Icon source={ChevronRightIcon} tone="subdued" />
          </span>
        )}
        <Text as="span" fontWeight="bold">
          {item}
        </Text>
      </span>
    ))}
  </span>
);

const StepIndicator = ({ current, label }: { current: 1 | 2; label: string }) => (
  <InlineStack gap="150" blockAlign="center">
    <span style={stepDotStyle(current === 1)} />
    <span style={stepDotStyle(current === 2)} />
    <Text as="span" variant="bodySm" tone="subdued">
      {label}
    </Text>
  </InlineStack>
);

const EditTrackingUrlButton = () => {
  const { shop, proxyPath } = useTrackingPageUrl();
  const dispatch = useDispatch();
  const authData = useSelector(tokenSelector);
  const [updateProxyPath, { isLoading: isSaving }] = useUpdateProxyPathMutation();
  const [syncTrackingUrlToShopify, { isLoading: isSyncing }] = useSyncTrackingUrlToShopifyMutation();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('choose');
  const [prefix, setPrefix] = useState<string>(PROXY_PREFIXES[0]);
  const [slug, setSlug] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [saveError, setSaveError] = useState('');

  const trimmedSlug = slug.trim();
  const slugError = validateProxySlug(trimmedSlug);
  const newPath = `${prefix}/${trimmedSlug}`;
  const newUrl = buildTrackingPageUrl(shop, newPath);

  const handleOpen = () => {
    const current = splitProxyPath(proxyPath);
    setPrefix(current.prefix);
    setSlug(current.slug);
    setConfirmed(false);
    setSaveError('');
    setStep('choose');
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleNext = () => {
    if (slugError) return;
    if (newPath === proxyPath) return handleClose();
    setStep('shopify');
  };

  const handleDone = async () => {
    setSaveError('');
    try {
      await updateProxyPath({ proxyPath: newPath }).unwrap();
      setStep('success');
    } catch (error) {
      setSaveError((error as { data?: { message?: string } })?.data?.message || 'Could not save the new URL. Please try again.');
    }
  };

  const handleUpdateOldUrl = async () => {
    setSaveError('');
    const res = await syncTrackingUrlToShopify({ shop: authData.shop });
    if ('data' in res && res.data && res.data.state === 1) {
      dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
      dispatch(settingsSlice.actions.handleTrackingLinkSyncCooldownEnd(Date.now() + 60 * 60 * 1000));
      handleClose();
      return;
    }
    setSaveError(getErrorFromAPI(res));
  };

  const handleSlugKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleNext();
    }
  };

  const modalActions: Record<Step, Partial<React.ComponentProps<typeof Modal>>> = {
    choose: {
      primaryAction: { content: 'Next step', onAction: handleNext, disabled: Boolean(slugError) },
      secondaryActions: [{ content: 'Cancel', onAction: handleClose }],
    },
    shopify: {
      footer: (
        <Button onClick={() => setStep('choose')} disabled={isSaving}>
          Back
        </Button>
      ),
      primaryAction: { content: 'Done', onAction: handleDone, disabled: !confirmed, loading: isSaving },
      secondaryActions: [{ content: 'Cancel', onAction: handleClose, disabled: isSaving }],
    },
    success: {
      primaryAction: { content: 'Update', onAction: handleUpdateOldUrl, loading: isSyncing },
    },
  };

  return (
    <>
      <Button onClick={handleOpen}>Edit URL</Button>
      <Modal open={open} onClose={handleClose} title={step === 'success' ? 'Update the old URL' : 'Edit tracking page URL'}
        {...modalActions[step]}>
        <Modal.Section>
          {step === 'choose' && (
            <BlockStack gap="400">
              <StepIndicator current={1} label="Step 1 of 2 · Choose your URL" />
              <InlineStack gap="200" wrap={false} blockAlign="start">
                <div style={{ flex: 5, minWidth: 0 }}>
                  <Select
                    label="URL prefix"
                    labelHidden
                    options={PROXY_PREFIXES.map((p) => ({ label: `https://${shop}/${p}/`, value: p }))}
                    value={prefix}
                    onChange={setPrefix}
                  />
                </div>
                <div style={{ flex: 2, minWidth: 0 }} onKeyDown={handleSlugKeyDown}>
                  <TextField
                    label="Page name"
                    labelHidden
                    value={slug}
                    onChange={setSlug}
                    autoComplete="off"
                    maxLength={40}
                    error={slugError || undefined}
                    autoFocus
                  />
                </div>
              </InlineStack>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">
                  URL will appear as
                </Text>
                <Box background="bg-surface-secondary" padding="200" borderRadius="200">
                  <span style={urlBoxStyle}>{slugError ? buildTrackingPageUrl(shop, `${prefix}/…`) : newUrl}</span>
                </Box>
              </BlockStack>
              <Text as="p" variant="bodySm" tone="subdued">
                Letters, numbers, hyphens and underscores only, up to 30 characters. “admin”, “services”, “password” and “login”
                aren’t allowed.
              </Text>
            </BlockStack>
          )}

          {step === 'shopify' && (
            <BlockStack gap="400">
              <StepIndicator current={2} label="Step 2 of 2 · Update Shopify" />
              <Banner tone="warning" title="Your customers can’t reach the page until Shopify matches.">
                <p>Until then, the new URL will show a 404.</p>
              </Banner>
              {saveError && (
                <Banner tone="critical">
                  <p>{saveError}</p>
                </Banner>
              )}
              <BlockStack gap="200">
                <List type="number">
                  <List.Item>
                    Open{' '}
                    <BreadcrumbPath items={['Settings', 'Apps', 'Synctrack OrderTrack']} />{' '}
                    in Shopify
                  </List.Item>
                  <List.Item>
                    Under{' '}
                    <Text as="span" fontWeight="bold">
                      App Proxy URL
                    </Text>
                    , select{' '}
                    <Text as="span" fontWeight="bold">
                      Customize URL
                    </Text>
                  </List.Item>
                  <List.Item>Enter exactly the URL below and save</List.Item>
                </List>
                <Box background="bg-surface-secondary" padding="200" borderRadius="200">
                  <span style={urlBoxStyle}>{newUrl}</span>
                </Box>
                <InlineStack>
                  <Button
                    icon={ExternalIcon}
                    onClick={() => window.open(LINK.SHOPIFY_APP_SETTINGS(shop), '_blank', 'noopener,noreferrer')}
                  >
                    Open Shopify settings
                  </Button>
                </InlineStack>
                <Checkbox
                  label="I’ve updated the App Proxy URL in Shopify to match this URL"
                  checked={confirmed}
                  onChange={setConfirmed}
                />
              </BlockStack>
            </BlockStack>
          )}

          {step === 'success' && (
            <BlockStack gap="400">
              {saveError && (
                <Banner tone="critical">
                  <p>{saveError}</p>
                </Banner>
              )}
              <BlockStack gap="200">
                <Text as="p" variant="bodyMd" fontWeight="bold">
                  Click Update to replace the old links in order details and order history for shipments from the last 30 days.
                </Text>
                <Text as="p" variant="bodyMd">
                  Note: New emails, SMS and order pages already use the new URL. Links already sent to customers still point to the
                  old URL.
                </Text>
              </BlockStack>
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
    </>
  );
};

export default EditTrackingUrlButton;
